import { Component, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { IOfferStep, OfferStepsDesktop, OfferStepsMobile } from '@app/enums/offer-steps.enum';
import { DeviceService } from '@app/services/device.service';
import { takeUntil } from 'rxjs/operators';
import { OptionInterface } from '@app/interfaces/option.interface';
import { Subject } from 'rxjs';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { HypoSelectComponent } from '@app/components/form-components/hypo-select/hypo-select.component';
import { MortgagesService } from '@app/services/mortgages.service';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

@Component({
    selector: 'app-admin-dialog',
    templateUrl: './admin-dialog.component.html',
    styleUrls: ['./admin-dialog.component.scss'],
    imports: [
        MatDialogModule,
        MatIconModule,
        MatTooltipModule,
        ReactiveFormsModule,
        MatButtonModule,
        HypoSelectComponent,
    ],
})
export class AdminDialogComponent implements OnInit, OnDestroy {
    protected readonly fb = inject(UntypedFormBuilder);
    protected readonly deviceService = inject(DeviceService);
    protected readonly renderer = inject(Renderer2);
    protected readonly mortgagesService = inject(MortgagesService);

    public matDialogRef = inject(MatDialogRef<AdminDialogComponent>);

    protected destroyed$: Subject<void> = new Subject<void>();

    public formGroup: UntypedFormGroup;
    stepList: IOfferStep[] = [];
    mortgage: MortgageInterface;
    public stepOptionList: OptionInterface[] = [];
    public isDesktop = true;

    get stepControl() {
        return this.formGroup.get('stepControl');
    }

    public close(): void {
        this.matDialogRef.close();
    }

    ngOnInit(): void {
        this.formGroup = this.fb.group({
            stepControl: [this.mortgagesService.mortgage?.fillingScore],
        });
        this.deviceService.isDesktopObservable.pipe(takeUntil(this.destroyed$)).subscribe((isDesktop: boolean) => {
            this.isDesktop = isDesktop;
            if (isDesktop) {
                this.stepList = OfferStepsDesktop;
            } else {
                this.stepList = OfferStepsMobile;
            }
            this.stepOptionList = this.stepList.map((step) => {
                return { label: step.name, value: step.fillingScore };
            });

            this.stepOptionList = this.stepOptionList.reduce((acc, cur) => {
                if (!acc.find((a) => a.value === cur.value)) {
                    acc.push(cur);
                }
                return acc;
            }, []);
        });

        this.mortgagesService.mortgage$.pipe(takeUntil(this.destroyed$)).subscribe((mortgage: MortgageInterface) => {
            this.mortgage = mortgage;
        });
    }

    ngOnDestroy(): void {
        this.destroyed$?.next();
        this.destroyed$?.complete();
    }

    public copyDealIdToClipboard(dealId: string, tooltip: MatTooltip) {
        navigator.clipboard.writeText(dealId);
        tooltip.disabled = false;
        tooltip.show();
        setTimeout(() => {
            tooltip.hide();
            tooltip.disabled = true;
        }, 1000);
    }

    changeStep(): void {
        if (this.mortgagesService.mortgage) {
            this.mortgagesService.mortgage.fillingScore = this.stepControl.value;
            this.mortgagesService.navigateToActualStep(this.mortgagesService.mortgage);
        }
        this.close();
    }

    public setDesktopMode(): void {
        this.deviceService.setDesktop(true);
        this.renderer.removeClass(document.body, 'body-mobile');
        this.mortgagesService.goHome();
        this.close();
    }

    public setMobileMode(): void {
        this.deviceService.setDesktop(false);
        this.renderer.addClass(document.body, 'body-mobile');
        this.mortgagesService.goHome();
        this.close();
    }
}
